import React, { useContext, useEffect, useState } from 'react';
import { withI18n } from '@lingui/react';
import { EventContext } from '../../Service/Context/EventContext';
import {
  fetchDocumentsByEvent,
  getTemplateUnresolvedPlaceholders,
  getUnresolvablePlaceholders,
} from '../../Service/restapi/documentService';
import { InputField } from '../../Atoms/InputFields';
import SelectContractTemplate from '../Event/Contracts/SelectContractTemplate';
import { UnresolvableInfoBox } from '../MailTemplates/UnresolvableInfoBox';
import { fetchContractsByEvent } from '../../Service/restapi/contractService';
import AttachmentListFileItem from './AttachmentListFileItem';
import SelectDocumentTemplate from './SelectDocumentTemplate';

export default withI18n()(
  ({ documentRequests, documentBlueprint, onDocumentRequestSelected, setDisableApply, i18n, history }) => {
    const { event } = useContext(EventContext);

    const [selectedDocumentRequest, setSelectedDocumentRequest] = useState(undefined);
    const [hasUnresolvedPlaceholders, setHasUnresolvedPlaceholders] = useState(false);
    const [selectedDocumentTemplate, setSelectedDocumentTemplate] = useState(undefined);

    const [newDocument, setNewDocument] = useState(false);

    const [existingDocuments, setExistingDocuments] = useState(undefined);

    useEffect(() => {
      if (documentBlueprint.documentType !== 'CONTRACT') {
        fetchDocumentsByEvent(event.id)
          .then((resp) => {
            if (resp.ok) {
              resp.json().then((documents) => {
                const tmpDocs = [];
                documents.forEach((d) => {
                  if (d.documentType !== documentBlueprint.documentType) {
                    tmpDocs.push(d);
                  }
                });
                setExistingDocuments(tmpDocs);
              });
            } else {
              console.error(`could not load documents of event ${event.id}`);
            }
          })
          .catch((error) => {
            console.error(`could not load documents of event ${event.id}`);
            console.error({ error });
          });
      } else {
        fetchContractsByEvent(event.id)
          .then((resp) => {
            if (resp.ok) {
              resp.json().then((documents) => {
                const tmpDocs = [];
                documents.forEach((d) => {
                  if (d.documentType !== documentBlueprint.documentType) {
                    tmpDocs.push(d);
                  }
                });
                setExistingDocuments(tmpDocs);
              });
            } else {
              console.error(`could not load contracts of event ${event.id}`);
            }
          })
          .catch((error) => {
            console.error(`could not load contracts of event ${event.id}`);
            console.error({ error });
          });
      }
    }, []);

    if (!documentRequests || !documentBlueprint) {
      console.error('documentRequests', documentRequests);
      console.error('documentBlueprint', documentBlueprint);
      throw new Error('documentRequests or documentBlueprint was null');
    }

    const styles = {
      container: {
        display: 'flex',
      },
      documentType: {
        flex: 1,
      },
      selectDocumentOrTemplate: {
        flex: 1,
      },
      selectDocumentTemplate: {
        marginTop: 5,
      },

      tableHead: {
        display: 'flex',
        justifyContent: 'space-between',
        fontWeight: 800,
        color: '#8ca3b5',
      },
      group: {
        marginBottom: 10,
      },
    };

    function onTemplateSelected(templateName) {
      if (templateName) {
        setDisableApply(true);
        setHasUnresolvedPlaceholders(false);

        getTemplateUnresolvedPlaceholders(documentBlueprint.documentType, templateName, event.id).then(
          (unresolvedPlaceholders) => {
            if (unresolvedPlaceholders.length > 0) {
              setSelectedDocumentTemplate(templateName);
              setHasUnresolvedPlaceholders(true);
            } else {
              setDisableApply(false);
            }
          },
        );

        const tmpDR = {
          ...selectedDocumentRequest,
          documentType: documentBlueprint.documentType,
          templateName,
          kind: 'GENERATE',
        };
        setSelectedDocumentRequest(tmpDR);
        onDocumentRequestSelected(tmpDR);
      } else {
        setDisableApply(false);
      }
    }

    function unselectDocumentRequest() {
      setSelectedDocumentRequest(undefined);
      onDocumentRequestSelected(undefined);
      setHasUnresolvedPlaceholders(false);
    }

    function documentRequestSelected(dR) {
      setSelectedDocumentRequest(dR);
      onDocumentRequestSelected(dR);
      setHasUnresolvedPlaceholders(false);

      if (!dR || dR.documentId === undefined) {
        setDisableApply(false);
      } else {
        setDisableApply(true);

        if (dR.documentId) {
          getUnresolvablePlaceholders(dR.documentType === 'CONTRACT' ? 'contracts' : 'documents', dR.documentId)
            .then((unresolvablePlaceholder) => {
              if (unresolvablePlaceholder.length > 0) {
                setHasUnresolvedPlaceholders(true);
              } else {
                setDisableApply(false);
              }
            })
            .catch((error) => {
              console.error(`could not get unresolvable placeholders of document ${dR.documentId}`);
              console.error({ error });
            });
        }
      }
    }

    return (
      <div>
        <div style={styles.container}>
          <div style={styles.selectDocumentOrTemplate}>
            {!newDocument && (
              <div style={styles.group}>
                {documentRequests && documentRequests.length > 0 && (
                  <div style={styles.tableHead}>
                    <div>{i18n._(`document.${documentBlueprint.documentType.toLowerCase()}`)} auswählen:</div>
                  </div>
                )}
                {documentRequests
                  ? documentRequests
                      .filter((dR) => dR.documentType === documentBlueprint.documentType)
                      .map((dR, index) => (
                        <AttachmentListFileItem
                          selected={selectedDocumentRequest === dR}
                          onClick={(item) => {
                            selectedDocumentRequest === dR ? unselectDocumentRequest() : documentRequestSelected(item);
                          }}
                          key={index}
                          item={dR}
                        />
                      ))
                  : null}
              </div>
            )}

            <InputField
              toggle={() => {
                documentRequestSelected(undefined);
                setNewDocument(!newDocument);
              }}
              checked={newDocument}
              text={i18n._(`event.documents.button.add.${documentBlueprint.documentType.toLowerCase()}`)}
            />

            <div style={styles.selectDocumentTemplate}>
              {/* if, 'new from document template' selected, show all possible document templates of this document type e.g. offer */}
              {newDocument && documentBlueprint.documentType !== 'CONTRACT' && (
                <SelectDocumentTemplate
                  documentType={documentBlueprint.documentType}
                  onTemplateSelected={onTemplateSelected}
                />
              )}
              {newDocument && documentBlueprint.documentType === 'CONTRACT' && (
                <SelectContractTemplate onChange={onTemplateSelected} />
              )}
            </div>

            {existingDocuments &&
              existingDocuments.length > 0 &&
              newDocument &&
              documentBlueprint.documentType !== 'CONTRACT' && (
                <div>
                  <br />
                  Positionen dabei aus vorhandenem Dokument übernehmen:
                </div>
              )}

            {newDocument &&
              existingDocuments &&
              existingDocuments.length > 0 &&
              documentBlueprint.documentType !== 'CONTRACT' && (
                <div style={styles.group}>
                  <div style={styles.tableHead}>
                    <div>Dokumente des Events</div>
                    <div>Erstellt</div>
                  </div>
                  {existingDocuments
                    ? existingDocuments.map((doc, index) => (
                        <AttachmentListFileItem
                          selected={selectedDocumentRequest ? selectedDocumentRequest.documentId === doc.id : false}
                          onClick={(doc) => {
                            if (selectedDocumentRequest && selectedDocumentRequest.documentId === doc.id) {
                              // unselect
                              setSelectedDocumentRequest(undefined);
                              documentRequestSelected(undefined);
                            } else {
                              setSelectedDocumentRequest({
                                ...documentBlueprint,
                                documentId: doc.id,
                                kind: 'GENERATE',
                              });
                              documentRequestSelected({ ...documentBlueprint, documentId: doc.id, kind: 'GENERATE' });
                            }
                          }}
                          key={index}
                          item={doc}
                        />
                      ))
                    : null}
                </div>
              )}
          </div>
        </div>

        {hasUnresolvedPlaceholders && (
          <UnresolvableInfoBox
            documentType={documentBlueprint.documentType}
            entityId={selectedDocumentRequest.documentId}
            selectedTemplate={selectedDocumentTemplate}
            eventId={event.id}
            customerId={event.customerId}
          />
        )}
      </div>
    );
  },
);
